import { useRef, useState } from 'react'
import { TASK_STATUS } from '@ovrsee/shared'
import { ApiError } from '../../lib/api.js'
import { FormModal } from '../../components/FormModal.js'
import { LoadingState } from '../../components/LoadingState.js'
import { ErrorState } from '../../components/ErrorState.js'
import { StatusChip } from '../../components/StatusChip.js'
import { FormError } from '../auth/FormError.js'
import { useReviewTask, useTaskDetail } from './useTasks.js'
import { Button, Input, Tab, TabList, Text, TextArea } from '../../ui/index.js'

const TABS = ['Submission', 'History', 'Notes'] as const

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${Math.round(kb)} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

function pretty(status: string | null) {
  return status === null ? 'nothing' : status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, ' ')
}

function describeEvent(type: string, fromStatus: string | null, toStatus: string | null): string {
  if (type === 'CREATED') return 'assigned the task'
  if (type === 'STATUS_CHANGED') return `moved it to ${pretty(toStatus)}`
  if (type === 'BLOCKED_FLAGGED') return 'flagged it as blocked'
  if (type === 'UNBLOCKED') return 'unblocked it'
  if (type === 'DEADLINE_EXTENDED') return 'extended the deadline'
  if (type === 'EXTERNAL_DELAY_FLAGGED') return 'flagged an external delay'
  if (type === 'COMPLEXITY_CHANGED') return 'changed the complexity'
  if (type === 'REASSIGNED') return 'reassigned it'
  return type.toLowerCase().replace(/_/g, ' ')
}

export function ReviewTaskModal({ taskId, onClose }: { taskId: string | null; onClose: () => void }) {
  const [tab, setTab] = useState<(typeof TABS)[number]>('Submission')
  // Held here rather than per-tab, so switching tabs cannot discard what was typed.
  const [reviewNote, setReviewNote] = useState('')
  const [newDeadline, setNewDeadline] = useState('')
  const [noteRequired, setNoteRequired] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const submitLock = useRef(false)

  const detail = useTaskDetail(taskId)
  const review = useReviewTask(taskId ?? '')

  const isAwaitingReview = detail.data?.status === TASK_STATUS.SUBMITTED
  const latestSubmission = detail.data?.submissions?.[0]

  async function decide(outcome: 'APPROVED' | 'RETURNED') {
    if (submitLock.current) return
    submitLock.current = true
    setFormError(null)
    setNoteRequired(false)

    if (outcome === 'RETURNED' && reviewNote.trim() === '') {
      // Mirrors the API rule. Returning a task with no explanation is useless to the
      // employee, so the UI refuses before the round trip.
      setNoteRequired(true)
      setTab('Notes')
      submitLock.current = false
      return
    }

    try {
      await review.mutateAsync({
        outcome,
        ...(reviewNote.trim() === '' ? {} : { reviewNote: reviewNote.trim() }),
        ...(newDeadline === '' ? {} : { newDeadlineAt: new Date(newDeadline).toISOString() }),
      })
      onClose()
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : 'Could not record that decision.')
      submitLock.current = false
    }
  }

  return (
    <FormModal
      title="Review task"
      isOpen={taskId !== null}
      onClose={onClose}
      onSubmit={() => void (isAwaitingReview ? decide('APPROVED') : onClose())}
      submitLabel={isAwaitingReview ? 'Approve' : 'Close'}
      canSubmit={true}
      busy={review.isPending}
    >
      {detail.isPending ? <LoadingState label="Loading task" /> : null}
      {detail.isError ? <ErrorState error={detail.error} onRetry={() => void detail.refetch()} /> : null}

      {detail.isSuccess ? (
        <>
          <FormError message={formError} />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: 'var(--o-space-5)' }}>
            <div style={{ minWidth: 0 }}>
              <div className="card-label">{detail.data.reference}</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--fg)', marginBottom: 'var(--o-space-4)' }}>
                {detail.data.title}
              </div>

              <TabList activeTabId={tab} onTabChange={(id) => setTab(id as (typeof TABS)[number])}>
                {TABS.map((label) => (
                  <Tab id={label} key={label}>
                    {label}
                  </Tab>
                ))}
              </TabList>

              <div style={{ paddingTop: 'var(--o-space-4)' }}>
                {tab === 'Submission' ? (
                  latestSubmission === undefined ? (
                    <Text color="secondary">Nothing has been submitted yet.</Text>
                  ) : (
                    <>
                      {latestSubmission.channel === 'EXTERNAL' ? (
                        <div
                          style={{
                            background: 'var(--info-bg)',
                            color: 'var(--info)',
                            borderRadius: 'var(--r-md)',
                            padding: 'var(--o-space-2) var(--o-space-3)',
                            fontSize: 12.5,
                            marginBottom: 'var(--o-space-3)',
                          }}
                        >
                          Completed outside Ovrsee
                        </div>
                      ) : null}
                      {latestSubmission.note !== null ? (
                        <p style={{ fontSize: 13, color: 'var(--fg-2)' }}>{latestSubmission.note}</p>
                      ) : null}
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {(detail.data.attachments ?? []).map((file) => (
                          <li
                            key={file.id}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              padding: 'var(--o-space-2) 0',
                              borderTop: '1px solid var(--divider)',
                              fontSize: 12.5,
                            }}
                          >
                            <span>{file.fileName}</span>
                            <span style={{ color: 'var(--fg-muted)' }}>{formatBytes(file.sizeBytes)}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )
                ) : null}

                {tab === 'History' ? (
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {(detail.data.events ?? []).map((event) => (
                      <li key={event.id} style={{ padding: 'var(--o-space-2) 0', fontSize: 12.5 }}>
                        <span style={{ fontWeight: 500, color: 'var(--fg)' }}>
                          {event.actor?.name ?? 'System'}
                        </span>{' '}
                        <span style={{ color: 'var(--fg-2)' }}>
                          {describeEvent(event.type, event.fromStatus, event.toStatus)}
                        </span>
                        {event.note !== null ? (
                          <div style={{ color: 'var(--fg-muted)' }}>&ldquo;{event.note}&rdquo;</div>
                        ) : null}
                        <div style={{ color: 'var(--fg-subtle)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                          {new Date(event.createdAt).toLocaleString()}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : null}

                {tab === 'Notes' ? (
                  <div className="field">
                    <label className="field-label" htmlFor="review-note">
                      Review note
                    </label>
                    <TextArea
                      id="review-note"
                      aria-label="Review note"
                      rows={4}
                      value={reviewNote}
                      onChange={setReviewNote}
                    />
                    {noteRequired ? (
                      <div role="alert" style={{ color: 'var(--negative)', fontSize: 12 }}>
                        Add a note so they know what to change.
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            <aside style={{ borderLeft: '1px solid var(--divider)', paddingLeft: 'var(--o-space-4)' }}>
              <Meta label="Status">
                <StatusChip status={detail.data.status} isOverdue={detail.data.isOverdue} />
              </Meta>
              <Meta label="Assignee">{detail.data.assignee?.name ?? '—'}</Meta>
              <Meta label="Complexity">
                <span style={{ fontFamily: 'var(--font-mono)' }}>{detail.data.complexity} / 10</span>
              </Meta>
              <Meta label="Due">
                <span style={{ fontFamily: 'var(--font-mono)' }}>{detail.data.dueIn}</span>
              </Meta>
              <Meta label="Category">{detail.data.category}</Meta>
            </aside>
          </div>

          {isAwaitingReview ? (
            <>
              <div className="field" style={{ marginTop: 'var(--o-space-4)' }}>
                <label className="field-label" htmlFor="new-deadline">
                  Extra time (optional)
                  <span style={{ position: 'absolute', left: -9999 }}>New deadline</span>
                </label>
                <Input
                  id="new-deadline"
                  type="datetime-local"
                  value={newDeadline}
                  onChange={setNewDeadline}
                  style={{
                    padding: '7px 10px',
                    borderRadius: 'var(--r-md)',
                    border: '1px solid var(--border)',
                    background: 'var(--surface)',
                    fontSize: 13,
                  }}
                />
                <Text color="secondary" style={{ fontSize: 12 }}>
                  Leave blank to keep the current deadline ({detail.data.dueIn === '—' ? 'Overdue' : detail.data.dueIn} remaining).
                </Text>
              </div>

              <div className="row-actions" style={{ marginTop: 'var(--o-space-4)' }}>
                <Button
                  type="button"
                  kind="secondary"
                  onClick={() => void decide('RETURNED')}
                  disabled={review.isPending}
                >
                  Return for correction
                </Button>
              </div>
            </>
          ) : (
            <Text color="secondary">This task is not awaiting review.</Text>
          )}
        </>
      ) : null}
    </FormModal>
  )
}

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 'var(--o-space-4)' }}>
      <div className="card-label">{label}</div>
      <div style={{ fontSize: 13, color: 'var(--fg-2)' }}>{children}</div>
    </div>
  )
}
